/**
 * Frontend Logger — Wraps @datadog/browser-logs with safe, crash-proof methods.
 *
 * This logger delegates to datadogLogs.logger (which is initialized in src/lib/datadog.js).
 * It provides:
 *   - Standard log levels (debug, info, warn, error) that map to Datadog's built-in levels
 *   - Global context management (setGlobalContext / clearGlobalContext)
 *   - Crash safety — every method is wrapped in try/catch so a logging failure
 *     never crashes the application
 *
 * Usage:
 *   import logger from '@/lib/logger';
 *   logger.info('User clicked submit', { page: 'transactions' });
 *   logger.error('API call failed', { url: '/dashboard' });
 *
 * On localhost, 127.0.0.1, or ngrok hosts (where datadog.js redirects datadogLogs.logger to console),
 * logs appear in browser DevTools instead of being sent to Datadog.
 */

import { datadogLogs } from '@datadog/browser-logs';

const consoleMethods = {
  debug: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error',
};

const isDatadogReady = () => Boolean(datadogLogs?.getInitConfiguration?.()) || datadogLogs?.logger === console;
const loggerContextKeys = new Set();

const writeToConsole = (level, args) => {
  const consoleMethod = consoleMethods[level] || 'log';
  // eslint-disable-next-line no-console
  (console[consoleMethod] || console.log)(`[${level.toUpperCase()}]`, ...args);
};

const safeLogMethod = (level) => {
  return (...args) => {
    try {
      if (isDatadogReady() && datadogLogs?.logger?.[level]) {
        datadogLogs.logger[level](...args);
      } else {
        writeToConsole(level, args);
      }
    } catch (logError) {
      console.error('Logger failed:', logError);
    }
  };
};

// Wrap the logger to ensure it never throws an error to parent component.
// We dont want our app to crash just because we're trying to log something.
const createSafeLogger = () => {
  return {
    debug: safeLogMethod('debug'),
    info: safeLogMethod('info'),
    warn: safeLogMethod('warn'),
    error: safeLogMethod('error'),

    setGlobalContext: (context) => {
      try {
        for (const [key, value] of Object.entries(context || {})) {
          loggerContextKeys.add(key);
          datadogLogs?.setGlobalContextProperty?.(key, value);
        }
      } catch (error) {
        console.error('Failed to set Datadog global context:', error);
      }
    },

    setGlobalContextProperty: (key, value) => {
      try {
        loggerContextKeys.add(key);
        datadogLogs?.setGlobalContextProperty?.(key, value);
      } catch (error) {
        console.error('Failed to set Datadog global context property:', error);
      }
    },

    removeGlobalContextProperty: (key) => {
      try {
        loggerContextKeys.delete(key);
        datadogLogs?.removeGlobalContextProperty?.(key);
      } catch (error) {
        console.error('Failed to remove Datadog global context property:', error);
      }
    },

    clearGlobalContext: () => {
      try {
        for (const key of loggerContextKeys) {
          datadogLogs?.removeGlobalContextProperty?.(key);
        }
        loggerContextKeys.clear();
      } catch (error) {
        console.error('Failed to clear Datadog global context:', error);
      }
    },
  };
};

// Export a singleton logger instance.
// Backward-compatible: existing code uses `import getLogger from './logger'; const logger = getLogger();`
// New code can also use `import logger from './logger';` directly (the default export IS the logger).
const logger = createSafeLogger();

const getLogger = () => logger;

export { logger };
export default getLogger;
